/**
 * /reports — Reports workspace. Surface Layer (/01)
 *
 * Lists report-generation jobs from `GET /api/reports` (typed reports_jobs
 * ledger) and enqueues new ones as `reports.generate` ActionFrames. Outputs
 * land once the execution worker writes them; until then jobs render their
 * `queued`/`running`/`succeeded` status + section count.
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  Grid,
  InlineNotification,
  SkeletonText,
  Tag,
  TextInput,
  Tile,
  preview__IconIndicator as IconIndicator,
} from '@carbon/react';
import { Report } from '@carbon/icons-react';
import type { ReportRow } from '@contracts/runtime-endpoints.contract';
import { useReports } from '@lib/persistence/useRuntimeData';
import { useActiveProjectStore } from '@lib/persistence/useActiveProject';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/reports')({
  component: ReportsView,
});

type JobStatus = ReportRow['status'];

// Carbon status-indicator kinds (verified against the installed @carbon/react).
const STATUS_KIND: Record<JobStatus, string> = {
  succeeded: 'succeeded',
  running: 'in-progress',
  queued: 'pending',
  requires_approval: 'caution-minor',
  failed: 'failed',
  cancelled: 'incomplete',
};

function ReportsView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const active = useActiveProjectStore((s) => s.active);
  const { reports, isLoading, tenantContextMissing, refetch } = useReports(undefined, active?.id);
  const [subject, setSubject] = useState('');
  const [result, setResult] = useState<{ status: string; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const refetchTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (refetchTimerRef.current !== null) window.clearTimeout(refetchTimerRef.current);
    },
    []
  );

  async function enqueue() {
    setBusy(true);
    const entry = await dispatch(
      'reports.generate',
      {
        subject: subject.trim() || 'Operational summary',
        range: 'last_30_days',
        ...(active ? { project_id: active.id } : {}),
      },
      { label: 'Generate report' }
    );
    setResult({ status: entry.status, detail: entry.detail });
    if (entry.status !== 'error') {
      refetchTimerRef.current = window.setTimeout(() => refetch(), 1200);
    }
    setBusy(false);
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Reports</h1>
        <p className="view__subtitle">Create operational reports and analytics-ready summaries.</p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile view-stack">
              <h2 className="view-tile__value">New report</h2>
              <TextInput
                id="reports-subject"
                labelText="Subject"
                placeholder="e.g. Weekly usage"
                value={subject}
                onChange={(event) => setSubject(event.currentTarget.value)}
              />
              <div className="view-actions">
                <Button renderIcon={Report} disabled={busy} onClick={() => void enqueue()}>
                  Generate
                </Button>
              </div>
              {result ? (
                <InlineNotification
                  kind={result.status === 'error' || result.status === 'denied' ? 'error' : 'info'}
                  lowContrast
                  hideCloseButton
                  title={result.status === 'error' || result.status === 'denied' ? 'Dispatch failed' : 'Enqueued'}
                  subtitle={result.detail}
                />
              ) : null}
            </Tile>
          </Column>

          {tenantContextMissing ? (
            <Column sm={4} md={8} lg={16}>
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                title="Workspace being provisioned"
                subtitle="Reports appear once your workspace context is ready."
              />
            </Column>
          ) : isLoading && reports.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <SkeletonText paragraph lineCount={4} />
            </Column>
          ) : reports.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-empty">
                <h2>No reports yet</h2>
                <p className="view-tile__label">Generate a report above to populate this workspace.</p>
              </Tile>
            </Column>
          ) : (
            reports.map((report) => (
              <Column key={report.id} sm={4} md={4} lg={4}>
                <Tile className="view-tile">
                  <div className="view-tile__row">
                    <strong>{report.title}</strong>
                    <IconIndicator kind={STATUS_KIND[report.status] ?? 'unknown'} label={report.status} size={16} />
                  </div>
                  <div className="notification-row__meta">
                    <span>{report.report_type}</span>
                    {report.period ? <Tag type="outline">{report.period}</Tag> : null}
                    <Tag type="cool-gray">{report.section_count} sections</Tag>
                    {report.output_uri ? <Tag type="teal">output ready</Tag> : null}
                  </div>
                  {report.error ? <p className="view-tile__label">Error: {report.error}</p> : null}
                </Tile>
              </Column>
            ))
          )}
        </Grid>
      </div>
    </div>
  );
}
